import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { countryFlag } from '../../constants/countryFlag'
import './styles/index.scss'
import { setItem } from '../../utils/storageUtils'
import { setSelectedLanguagesStart } from '../../store/redux-slices/language'
import { getLanguageCodeLabel, getLanguageDisplayName } from 'utils/language'
import { useNavigate } from 'react-router-dom'

/**
 * A component that renders a language selection dropdown menu
 *
 * @param {Object} props Component props
 * @param {Function} props.setLanguage Function to set the selected language
 *   Defaults to true when more than one language is available.
 * @returns {JSX.Element} Language selection dropdown component
 */
const LanguageSelect = ({ setLanguage }) => {
  const { selectedLanguageCode, languages, languageData } = useSelector(
    (state) => state.language
  )
  const general = useSelector((state) => state.uiConfig?.general)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const hasMultipleLanguages = (languages?.allowedLanguages?.length || 0) > 1
  const shouldShowDropdownIcon = hasMultipleLanguages

  return (
    <div className={`dropdown languages${shouldShowDropdownIcon ? ' show' : ''}`}>
      <button
        type='button'
        className={`dropdown-toggle${!shouldShowDropdownIcon ? ' no-caret' : ''}`}
        data-toggle={shouldShowDropdownIcon ? 'dropdown' : undefined}
        aria-expanded='false'
        aria-disabled={!shouldShowDropdownIcon}
        disabled={!shouldShowDropdownIcon}
        style={{
          fontSize: '14px',
          fontWeight: 'normal'
        }}
      >
        <span>
          {countryFlag?.filter(
            (ele) => ele?.languageCode === selectedLanguageCode
          )?.[0]?.flag && (
            <img
              width='25px'
              className='me-1'
              src={`${
                countryFlag?.filter(
                  (ele) => ele?.languageCode === selectedLanguageCode
                )?.[0]?.flag
              }`}
              alt='country-img'
            />
          )}
        </span>
        {getLanguageCodeLabel(selectedLanguageCode, general)}{shouldShowDropdownIcon ? ' |' : ''}
      </button>

      {shouldShowDropdownIcon && (
        <div className='dropdown-menu dropdown-menu-right'>
          {languages?.allowedLanguages?.map(
            (item, i) =>
              selectedLanguageCode !== item && (
                <a
                  key={i}
                  className='dropdown-item user'
                  onClick={() => {
                    setLanguage(item.toLowerCase())
                    setItem('language', item.toUpperCase())
                    dispatch(
                      setSelectedLanguagesStart({
                        selectedLanguageCode: item,
                        navigate
                      })
                    )
                  }}
                >
                  {countryFlag?.filter((ele) => ele?.languageCode === item)?.[0]
                    ?.flag && (
                      <img
                        width='25px'
                        src={`${
                        countryFlag?.filter(
                          (ele) => ele?.languageCode === item
                        )?.[0]?.flag
                      }`}
                        className='me-2'
                        alt='country-img'
                      />
                  )}
                  {getLanguageDisplayName(item, selectedLanguageCode, languageData, general)}
                </a>
              )
          )}
        </div>
      )}
    </div>
  )
}

/**
 * A component that renders the language selection list
 *
 * @param {Object} props Component props
 * @param {string} props.selectedLanguageCode Currently selected language code
 * @param {Object} props.languages Available languages object
 * @param {Function} props.setLanguage Function to set the selected language
 * @param {Function} props.dispatch Redux dispatch function
 * @param {Function} props.navigate React router navigate function
 * @param {boolean} props.isSidebar Whether the component is rendered in sidebar
 * @param {Object} props.general Tenant config (`state.uiConfig.general`)
 * @returns {JSX.Element} Language dropdown list component
 */
const LangDropdown = ({
  selectedLanguageCode,
  languages,
  setLanguage,
  dispatch,
  navigate,
  isSidebar,
  languageData,
  general
}) => (
  <div className='language-name'>
    {!isSidebar && (
      <div className='name selected'>
        {countryFlag?.filter(
          (ele) => ele?.languageCode === selectedLanguageCode
        )?.[0]?.flag && (
          <img
            width='25px'
            src={`${
              countryFlag?.filter(
                (ele) => ele?.languageCode === selectedLanguageCode
              )?.[0]?.flag
            }`}
            alt='country-img'
          />
        )}
        {getLanguageDisplayName(selectedLanguageCode, selectedLanguageCode, languageData, general)}
      </div>
    )}
    {languages?.allowedLanguages?.map(
      (item, i) =>
        item !== selectedLanguageCode && (
          <div
            className={`name ${
              selectedLanguageCode?.toUpperCase() === item?.toUpperCase()
                ? 'selected'
                : ''
            }`}
            key={i}
            onClick={() => {
              setLanguage(item.toLowerCase())
              setItem('language', item.toUpperCase())
              dispatch(
                setSelectedLanguagesStart({
                  selectedLanguageCode: item,
                  navigate
                })
              )
            }}
          >
            {countryFlag?.filter((ele) => ele?.languageCode === item)?.[0]
              ?.flag && (
                <img
                  width='25px'
                  src={`${
                  countryFlag?.filter((ele) => ele?.languageCode === item)?.[0]
                    ?.flag
                }`}
                  alt='country-img'
                />
            )}
            {getLanguageDisplayName(item, selectedLanguageCode, languageData, general)}
          </div>
        )
    )}
  </div>
)

/**
 * A component that renders a language dropdown at the bottom of the page
 * @param {Object} props Component props
 * @param {string} props.language Current language
 * @param {Function} props.setLanguage Function to set the selected language
 * @param {boolean} props.isFooter Whether the component is rendered in footer
 * @param {boolean} [props.isSidebar=false] Whether the component is rendered in sidebar
 *   Defaults to true when more than one language is available.
 * @returns {JSX.Element} Bottom language dropdown component
 */
export const BottomLanguageDropdown = ({
  language,
  setLanguage,
  isFooter,
  isSidebar = false
}) => {
  const { languages, selectedLanguageCode, languageData } = useSelector(
    (state) => state.language
  )
  const general = useSelector((state) => state.uiConfig?.general)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const hasMultipleLanguages = (languages?.allowedLanguages?.length || 0) > 1
  const shouldShowDropdownIcon = hasMultipleLanguages

  const activeLanguageName = getLanguageDisplayName(
    selectedLanguageCode,
    selectedLanguageCode,
    languageData,
    general
  )

  return (
    <div
      className={`dropdown language-dropdown-bottom  ${isFooter && 'footer'} `}
    >
      <button
        className={`language-btn text-left dropdown-toggle text-center name${!shouldShowDropdownIcon ? ' no-caret' : ''}`}
        id='dropdownLanguageButtonFooter'
        data-toggle={shouldShowDropdownIcon ? 'dropdown' : undefined}
        aria-expanded='false'
        aria-disabled={!shouldShowDropdownIcon}
        disabled={!shouldShowDropdownIcon}
      >
        {countryFlag?.filter(
          (ele) => ele?.languageCode === selectedLanguageCode
        )?.[0]?.flag && (
          <img
            width='25px'
            src={`${
              countryFlag?.filter(
                (ele) => ele?.languageCode === selectedLanguageCode
              )?.[0]?.flag
            }`}
            alt='country-img'
            className={`${isSidebar && 'sidebar-img'}`}
          />
        )}
        {activeLanguageName}
      </button>
      {shouldShowDropdownIcon && (
        <div
          aria-labelledby='dropdownLanguageButtonFooter'
          className={`dropdown-menu  ${
            isSidebar ? 'sidebar-language' : 'language-selector-hide'
          }`}
          id='dropdownLanguageButtonFooter'
        >
          <LangDropdown
            selectedLanguageCode={selectedLanguageCode}
            languages={languages}
            setLanguage={setLanguage}
            dispatch={dispatch}
            language={language}
            languageData={languageData}
            general={general}
            navigate={navigate}
            isSidebar={isSidebar}
          />
        </div>
      )}
    </div>

  )
}

export default LanguageSelect
